import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestion des importations";
const heroImage = "https://net-basis.net/Bilder/import-management-customs-clearance-dach-distribution.webp";
const heroAlt = "Des conteneurs empilés symbolisent la gestion des importations, le dédouanement et la vente locale.";
const heroClaimLine1 = "Bien plus que du";
const heroClaimLine2 = "simple transport !";
const benefitsTitle = "Comment vous profitez de notre expertise en __gestion des importations__ :";
const introText = "Une gestion efficace des importations pour votre accès transparent au marché de la région DACH. En tant qu'experts locaux, nous agissons pour les fabricants étrangers de TIC comme un service d'importation local pleinement intégré et un lien commercial fort vers vos marchés cibles.";
const p1Text = "NetBasis International sert de service d'importation local sur place pour ses fabricants partenaires étrangers. Nous prenons en charge toutes les activités d'importation essentielles ainsi que les efforts de vente et de marketing au sein de l'espace économique germanophone, permettant à nos partenaires de bénéficier directement de notre réseau local et d'un accès immédiat aux bons partenaires commerciaux.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Prise en charge complète de l'ensemble du processus d'importation, y compris le dédouanement professionnel",
  "Classification correcte des produits selon les codes tarifaires et échange fluide d'informations avec tous les partenaires commerciaux",
  "Garantie fiable de la conformité du marquage et de l'emballage des produits selon les spécificités nationales",
  "Exécution légale de tous les processus dans le strict respect des interdictions et restrictions locales",
  "Couverture complète et minimisation de l'ensemble des risques financiers et juridiques sur place",
  "Logistique irréprochable et livraison ponctuelle aux négociants ainsi qu'aux distributeurs locaux",
  "Mise en œuvre stratégique de campagnes publicitaires ciblées, de marketing et d'actions promotionnelles",
  "Planification, organisation et réalisation professionnelles de salons ou congrès pertinents, ainsi que participation à ces événements",
  "Prise en charge et gestion fiables de l'intégralité de la facturation et de la comptabilité financière locale",
];

export default function GestionDesImportationsFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestion des Importations | NetBasis International"
        description="Traitement fluide, coordination logistique et respect de toutes les réglementations locales pour vos flux de marchandises internationaux."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/gestion-des-importations"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start overflow-hidden pointer-events-none pt-10 sm:pt-16 md:pt-[58px] lg:pt-[72px] pl-8 sm:pl-8 md:pl-[62px] lg:pl-14">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left importmanagement-hero-text-slide im-hero-lh-15"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  <span className="hidden md:inline">&nbsp;</span>
                  <span className="block md:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {introText}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}